import { api } from './api.svelte.js';

const STORAGE_KEY = 'smacos.portfolio';

const defaults = {
  profile: {
    name: 'Muhammad Faizan Anwar',
    headline: 'Systems & AI Engineer',
    bio: 'I build AI and LLM products, concurrent Go backends, and modern full-stack apps, shipping production software for clients in Belgium, Russia and wherever else the work is interesting.',
    location: 'Islamabad, Pakistan · remote-friendly',
    email: 'raomuhammadfaizananwar@gmail.com',
    github: 'https://github.com/m-faizananwar',
    linkedin: 'https://www.linkedin.com/in/muhammad-faizan-anwar-67a480280/',
  },
  resume: {
    summary:
      'Systems and AI engineer, CS undergraduate at NUST SEECS, and founder of Zune Studios. Ships production AI products end to end: generative-media pipelines, RAG and agentic LLM systems, concurrent Go backends and cross-platform product work, delivered for clients in Belgium, Russia and the US.',
    skills: [
      'TypeScript',
      'Python',
      'Go',
      'Next.js / React',
      'Svelte 5',
      'Flutter / Dart',
      'Django / DRF',
      'LangChain / RAG',
      'LLM Agents',
      'PostgreSQL / Supabase',
      'Docker / CI',
      'Kubernetes',
    ],
    education: [
      {
        degree: 'BS Computer Science',
        school: 'NUST SEECS (School of Electrical Engineering & Computer Science)',
        place: 'Islamabad, Pakistan',
        period: '2023 to 2027 (expected)',
      },
    ],
    experience: [
      {
        role: 'Software Engineer',
        org: 'Propivue',
        link: 'https://propivue.com/',
        period: '2026 to present',
        tags: ['Next.js', 'React', 'FFmpeg', 'Generative video', 'Postgres', 'Railway'],
        detail:
          'An AI marketing platform for real estate. An agent or short-let host uploads listing photos and gets back a finished, narrated video ad. I built the generation pipeline end to end: clips per photo, then automated FFmpeg assembly with voiceover, burned captions, branding and a music mix, streamed to the browser with live progress. Ten AI video and speech providers sit behind one adapter layer with per-shot routing, so a model can be swapped without touching the product.',
      },
      {
        role: 'Product Engineer',
        org: 'Particula Tech',
        link: 'https://particula.tech/',
        period: 'Jun 2025 to Mar 2026',
        tags: ['RAG', 'Agentic pipelines', 'Model reports', 'OpenRouter', 'n8n', 'Bitrix24'],
        detail:
          'Product engineer on the AI side. I basically lived inside RAG and agentic pipelines, taught a pile of models to write reports a human would actually read, and shipped the unglamorous glue that keeps AI products from doing something stupid in production. Also automated the boring parts of the business with n8n: Bitrix24 CRM pipelines, email sequences and cross-system integrations.',
      },
      {
        role: 'ML / Computer Vision Intern',
        org: 'MachVis Lab',
        link: 'https://www.linkedin.com/company/machvis/',
        period: 'Summer 2025',
        tags: ['Deep Learning', 'YOLO', 'LoRA', 'Computer Vision', 'IoT', 'DeepFace'],
        detail:
          'A summer spent teaching machines to see. Went deep on YOLO, LoRA fine-tuning and a bit of IoT, and built a face-recognition attendance system that can tell a real face from a printed photo held up to the camera.',
      },
      {
        role: 'Full-stack Engineer',
        org: 'LYTE Studios',
        link: 'https://lytestudios.be/',
        period: 'Dec 2024 to Jun 2025',
        tags: ['Flutter', 'Django + DRF', 'Celery + Redis', 'Melos monorepo', 'AWS ECS', 'Sentry'],
        detail:
          'A Belgian software studio. I built Flutter apps and their web CMS on a Django backend, then went spelunking into a social-security payroll pipeline until it stopped failing silently. Also stood up auth and booking flows that people actually pay money through, and contributed to Jobr, a job-matching app rolling out across Belgian municipalities.',
      },
      {
        role: 'Chatbot / Flutter Intern',
        org: 'Logixos',
        period: 'Jun to Sep 2024',
        tags: ['Flutter', 'Riverpod', 'BLoC', 'Provider', 'Monorepo', 'Chatbots'],
        detail:
          'My first real Flutter job, mostly spent convincing chatbots to behave. Riverpod, Provider and BLoC across a shared monorepo, and I shipped a lawyer bot, a query bot and a NUST campus assistant so students could stop asking humans.',
      },
    ],
    // Presented separately from the formal timeline: the "for fun" track.
    sideQuests: {
      label: 'Off the clock',
      detail:
        'A self-hosted homelab where an Oracle VPS quietly runs my own WireGuard VPN, plus whatever side project has currently hijacked my weekend.',
    },
  },
  projects: [
    {
      id: 'atlas',
      title: 'Atlas',
      type: 'AI DevOps platform',
      status: 'Live demo',
      year: '2026',
      summary:
        'An AI layer over software delivery. It watches a deployment break, investigates against real evidence rather than vibes, plays out what-if branches, then makes an agent prove its repair with a red-to-green regression test before a human is allowed to hit recover. It cloned a real production app, reproduced an actual build failure and opened a verified pull request. Its Infrastructure Architect reads a repo and writes validated Terraform for AWS, GCP and Azure in a credential-less container, then refuses to call it ready until it has been checked for data loss, public exposure, runaway spend and over-broad IAM. It stops at a plan you own, because replace-versus-update is not a guess worth making on your database.',
      stack: ['Next.js', 'OpenAI Agents SDK', 'Terraform'],
      tags: ['Agentic AI', 'Codex SDK', 'Terraform policy checks', 'Evidence-grounded RCA', 'Self-hosted'],
      link: 'https://atlas.m-faizananwar.com',
    },
    {
      id: 'flowra',
      title: 'Flowra',
      type: 'AI platform · Lead dev',
      status: 'Live',
      year: '2026',
      summary:
        'An AI project manager that actually checks your homework. It reads the team chatter across Discord, Slack and Telegram, cross-references it against real GitHub activity to confirm the work truly shipped, then proposes approval-first Jira updates. It watches how every role is doing, not just the coders. I led a six-person team on this one.',
      stack: ['Octokit', 'Vercel', 'Postgres'],
      tags: ['AI Agents', 'Gemini + Groq', 'GitHub + Jira automation', 'Turborepo monorepo', 'Self-hosted on VPS'],
      link: 'https://flowra.m-faizananwar.com',
    },
    {
      id: 'projectflow-ai',
      title: 'ProjectFlow AI',
      type: 'Particula Tech',
      status: 'Live (beta)',
      year: '2026',
      summary:
        'A B2B brain for project teams. Multi-model agents chew through your chats and docs, then hand back tasks, risks, performance and forecasts before the standup even starts. My strongest commercial AI work so far.',
      stack: ['Next.js 16', 'Postgres'],
      tags: ['RAG', 'Agentic pipelines', 'OpenRouter multi-model', 'Streaming', 'Redis', 'Stripe'],
      link: 'https://projectflowai.io/',
    },
    {
      id: 'pictora',
      title: 'Pictora',
      type: 'Android app · Flagship',
      status: 'Released',
      year: '2026',
      summary:
        'Photo search for your phone that never phones home. Describe a picture in plain English or hand it an example image, and CLIP-style encoders find it, all completely offline. There is even an Ask Pictora chat that plans the search for you. Honestly my favorite thing I have built.',
      stack: ['Kotlin', 'Koin DI'],
      tags: ['CLIP / MobileCLIP2', 'ONNX + LiteRT', 'ObjectBox HNSW vectors', 'INT8 quantization', 'On-device, fully offline'],
      link: 'https://github.com/m-faizananwar/Pictora',
    },
    {
      id: 'synapse',
      title: 'Synapse',
      type: 'Distributed systems',
      status: 'Open source',
      year: '2025',
      summary:
        'Distributed tracing built from scratch in Go, because just reading the textbook felt like cheating. A sidecar proxy stamps vector clocks between services, samples with Bloom filters, and commits to a Raft cluster so everyone agrees on what actually happened. Ships with a PySpark anomaly hunt for good measure.',
      stack: ['Go 1.22', 'BoltDB', 'Prometheus'],
      tags: ['HashiCorp Raft', 'Vector clocks', 'Bloom-filter sampling', 'Kafka + PySpark', 'Kubernetes'],
      link: 'https://github.com/m-faizananwar/Synapse',
    },
    {
      id: 'clone-facen',
      title: 'clone-facen',
      type: 'Computer vision · MachVis Lab',
      status: 'Open source',
      year: '2025',
      summary:
        'Attendance that just looks at you. Point a webcam, it grabs a face embedding, matches it to the roster and marks you present with a timestamp. Everything runs locally, and a liveness check means holding up a printed photo will not get you a free day off.',
      stack: ['Flask', 'TensorFlow'],
      tags: ['DeepFace + GhostFaceNet', 'OpenCV', 'Liveness / anti-spoof', 'Runs fully offline'],
      link: 'https://github.com/m-faizananwar/clone-facen',
    },
    {
      id: 'lumora',
      title: 'Lumora',
      type: 'Agentic AI · Accessibility',
      status: 'Prototype',
      year: '2026',
      summary:
        'An AI co-pilot for blind and low-vision users. Point your phone and a five-agent pipeline (perceive, classify, plan, decide, act) turns the camera into a real-time navigation buddy that calls out hazards and directions out loud.',
      stack: ['Flutter', 'FastAPI'],
      tags: ['Multi-agent orchestration', 'Gemini 2.5 Flash', 'YOLO on-device', 'Google Antigravity', 'Accessibility tech'],
      link: 'https://github.com/MuhammadAhmed43/Lumora',
    },
    {
      id: 'hogyoku-vpn',
      title: 'Self-hosted VPN',
      type: 'Infrastructure',
      status: 'Running',
      year: '2025',
      summary:
        'My own private VPN, because handing all my traffic to someone else felt a little silly. A WireGuard tunnel living on an Oracle VPS that I run and babysit myself, zero logs, full speed, and a convenient way to make geo-blocks quietly disappear.',
      stack: ['WireGuard', 'Oracle VPS', 'Linux'],
      tags: ['WireGuard', 'Self-hosted networking', 'Zero-logs privacy', 'VPS ops'],
      link: '',
    },
    {
      id: 'onhealth',
      title: 'OnHealth',
      type: 'Particula Tech',
      status: 'Live',
      year: '2026',
      summary:
        'Snap your dinner, get your macros. A vision model reads a photo of your meal and rebuilds a personalized plan around it, complete with recipes and a shopping list. Speaks both English and Russian, so it never judges you in only one language.',
      stack: ['Next.js'],
      tags: ['Vision-LLM meal analysis', 'OpenRouter', 'Row-level security', 'Bilingual EN / RU'],
      link: 'https://on-health.ru/',
    },
    {
      id: 'get-a-wash',
      title: 'Get A Wash / Werkr',
      type: 'LYTE Studios',
      status: 'Live',
      year: '2025',
      summary:
        'Think Uber, but for getting cars washed in Belgium. Customer and worker Flutter apps plus a web CMS on a Django backend, including the deeply unglamorous social-security payroll plumbing that has to be exactly right or nobody gets paid.',
      stack: ['Flutter', 'Postgres'],
      tags: ['Django + DRF', 'Celery + Redis', 'Melos monorepo', 'AWS ECS', 'Sentry'],
      link: 'https://getawash.lytestudios.be/',
    },
    {
      id: 'tinrate',
      title: 'Tinrate',
      type: 'LYTE Studios',
      status: 'Live',
      year: '2026',
      summary:
        'Turn your DMs into paid calls. A Flutter-web marketplace where you book one-on-one video sessions with vetted experts. I wired up the auth and the whole booking flow so money and calendars actually line up.',
      stack: ['Flutter Web'],
      tags: ['Mollie payments', 'Google Meet', 'Booking engine', 'Sentry'],
      link: 'https://tinrate.io/',
    },
    {
      id: 'mogdesk',
      title: 'MogDesk',
      type: 'SaaS product · Founder',
      status: 'Live',
      year: '2026',
      summary:
        'A helpdesk SaaS I started and still run the front of. Every customer gets their own walled-off tenant with its own tickets and users, plus a Gemini assistant that drafts replies so support agents do not slowly lose their minds.',
      stack: ['Next.js 15', 'Postgres'],
      tags: ['Multi-tenant (schema isolation)', 'django-ninja API', 'Gemini assistant', 'Auth + MFA', 'Sentry'],
      link: 'https://mogdesk.com',
    },
    {
      id: 'aquaos',
      title: 'AquaOS',
      type: 'This website',
      status: 'You are here',
      year: '2026',
      summary:
        'The thing you are clicking right now. A whole Mac OS X desktop faked in the browser, with draggable windows, a Dock, a menu bar and around sixty little apps, all backed by a real Go server running the live chat behind the curtain.',
      stack: ['Svelte 5', 'Vite'],
      tags: ['Go WebSocket backend', 'SQLite to Postgres', 'S3 + GeoIP', 'gosec scanning', 'Docker'],
      link: 'https://github.com/m-faizananwar/aquaos',
    },
  ],
  settings: {
    desktopTint: 'graphite',
    wallpaper: null,
    showDesktopIcons: true,
    autoHideDock: true,
    dockPosition: 'bottom',
    dockSize: 52,
    dockMagnification: true,
    reduceTransparency: false,
    reduceMotion: false,
    highContrast: false,
    textScale: 1,
    alertVolume: 0.7,
    autoRearrangeSpaces: true,
    groupWindowsByApp: true,
    dashboardAsSpace: false,
    dockOrder: null,
    shortcuts: {},
    notificationsEnabled: true,
    notificationTimeout: 5000,
  },
};

function copy(value) {
  return JSON.parse(JSON.stringify(value));
}

// Bump when the built-in profile/resume/projects content changes shape or
// substance: stale cached content is replaced while user settings survive.
const CONTENT_VERSION = 7;

function mergeContent(saved) {
  saved = saved && typeof saved === 'object' ? saved : {};
  if (saved.contentVersion !== CONTENT_VERSION) {
    return {
      contentVersion: CONTENT_VERSION,
      profile: copy(defaults.profile),
      resume: copy(defaults.resume),
      projects: copy(defaults.projects),
      settings: { ...defaults.settings, ...(saved.settings ?? {}) },
    };
  }
  return {
    contentVersion: CONTENT_VERSION,
    profile: { ...defaults.profile, ...(saved.profile ?? {}) },
    resume: {
      ...defaults.resume,
      ...(saved.resume ?? {}),
      skills: Array.isArray(saved.resume?.skills) ? saved.resume.skills : defaults.resume.skills,
      experience: Array.isArray(saved.resume?.experience) ? saved.resume.experience : defaults.resume.experience,
    },
    projects: Array.isArray(saved.projects) ? saved.projects : defaults.projects,
    settings: { ...defaults.settings, ...(saved.settings ?? {}) },
  };
}

function loadLocal() {
  if (typeof localStorage === 'undefined') return copy(defaults);
  try {
    return mergeContent(JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}'));
  } catch {
    return copy(defaults);
  }
}

function slugify(value) {
  const slug = String(value).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return slug || `project-${Date.now()}`;
}

export function splitList(value) {
  return String(value ?? '').split(',').map(item => item.trim()).filter(Boolean);
}

function projectToBackend(p) {
  return {
    title: p.title ?? '',
    description: p.summary ?? '',
    url: p.link ?? '',
    tags: JSON.stringify(p.stack ?? []),
    type: p.type ?? 'Web app',
    status: p.status ?? 'Draft',
    year: p.year ?? '',
    slug: p.id ?? slugify(p.title ?? 'project'),
    sort_order: p.sort_order ?? 0,
  };
}

function projectFromBackend(b) {
  let stack = [];
  try { stack = JSON.parse(b.tags ?? '[]'); } catch { stack = []; }
  return {
    id: b.slug || slugify(b.title),
    title: b.title ?? '',
    type: b.type ?? 'Web app',
    status: b.status ?? 'Draft',
    year: b.year ?? '',
    summary: b.description ?? '',
    stack: Array.isArray(stack) ? stack : [],
    link: b.url ?? '',
    sort_order: b.sort_order ?? 0,
  };
}

export const portfolio = $state(loadLocal());

let _saveTimer = null;

export function savePortfolio() {
  if (typeof localStorage === 'undefined') return;
  clearTimeout(_saveTimer);
  _saveTimer = setTimeout(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(portfolio));
  }, 300);
}

export async function syncFromAPI() {
  try {
    const [profile, projects] = await Promise.all([
      api.getProfile().catch(() => null),
      api.listProjects().catch(() => null),
    ]);
    if (profile) {
      portfolio.profile = {
        ...portfolio.profile,
        name: profile.name ?? portfolio.profile.name,
        bio: profile.bio ?? portfolio.profile.bio,
        email: profile.email ?? portfolio.profile.email,
        location: profile.location ?? portfolio.profile.location,
      };
    }
    // Only adopt backend projects when it actually has some — an empty
    // backend list must not wipe the built-in portfolio defaults.
    if (Array.isArray(projects) && projects.length) {
      portfolio.projects = projects.map(projectFromBackend);
    }
  } catch {
    // API unavailable — keep localStorage data
  }

  try {
    const settings = await api.getSettings().catch(() => null);
    if (settings && typeof settings === 'object') {
      portfolio.settings = { ...portfolio.settings, ...settings };
    }
  } catch {
    // keep local settings
  }
}

export async function resetPortfolio() {
  const fresh = copy(defaults);
  portfolio.profile = fresh.profile;
  portfolio.resume = fresh.resume;
  portfolio.projects = fresh.projects;
  portfolio.settings = fresh.settings;
  savePortfolio();
}

export function updateProfile(profile) {
  portfolio.profile = { ...portfolio.profile, ...profile };
  savePortfolio();
  api.updateProfile({
    name: profile.name,
    bio: profile.bio,
    email: profile.email,
    location: profile.location,
  }).catch(() => {});
}

export function updateResume(resume) {
  portfolio.resume = { ...portfolio.resume, ...resume };
  savePortfolio();
}

export function updateSettings(settings) {
  portfolio.settings = { ...portfolio.settings, ...settings };
  savePortfolio();
  api.updateSettings(settings).catch(() => {});
}

export function upsertProject(project) {
  const clean = {
    ...project,
    id: project.id || slugify(project.title || 'project'),
    stack: Array.isArray(project.stack) ? project.stack : splitList(project.stack),
  };
  const i = portfolio.projects.findIndex(p => p.id === clean.id);
  if (i === -1) portfolio.projects.push(clean);
  else portfolio.projects[i] = clean;
  savePortfolio();

  api.createProject(projectToBackend(clean)).catch(() => {});
  return clean.id;
}

export function deleteProject(id) {
  const i = portfolio.projects.findIndex(p => p.id === id);
  if (i !== -1) portfolio.projects.splice(i, 1);
  savePortfolio();
  api.deleteProject(id).catch(() => {});
}
